import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { requestMapper } from "selectors/RequestSelector";
import { connect } from "react-redux";
import HeaderView from "./HeaderView2";
import SidebarView from "./SidebarView2";
import { themeSelector } from "../design/theme";
import { makeStyles, ThemeProvider } from "@material-ui/core/styles";
import clsx from "clsx";
import CssBaseline from "@material-ui/core/CssBaseline";
import AppBar from "@material-ui/core/AppBar";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MuiErrorBanner from "./Components/MuiErrorBanner";
import Zendesk from "./Components/Zendesk";
import LicenseRestriction from "views/Components/License";
import AutoIsolationControlBar from "./Components/AutoIsolation/ControlBar";
import { getLanguageDictionary } from "../lib/helpers";
import Request from "lib/api/Request";
import { renderSpacerGridItem } from "../utils/sidebarUtils";
import {
  ORG_TYPES,
  MARKETS,
  LICENSE_TYPES,
} from "./Pages/OrganizationPageView";
import Cookies from "universal-cookie";
import _ from "lodash";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    width: "100%",
    flexGrow: 1,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarSpacer: theme.mixins.toolbar,
  grid: {
    margin: 0,
    width: "calc(100% - 220px)",
  },
  pageTitle: {
    margin: theme.spacing(0, 1, 0, 2),
    verticalAlign: "center",
    display: "inline-block",
  },
  pageSubTitle: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 3, 0),
  },
  pageTitleDivider: {
    margin: theme.spacing(1, 0, 0, 0),
  },
  gridItem: {
    width: "100%",
  },
}));

const language = getLanguageDictionary("en");

const darkTheme = "shadow";
const lightTheme = "default";

const setCustomerInfoCookie = (orgDetails = {}) => {
  const { type, market: channel, license } = orgDetails;
  const cookies = new Cookies();
  cookies.set(
    "orgDetails",
    JSON.stringify({
      type: type,
      channel: channel,
      license: license,
    }),
    {
      path: "/",
      domain: ".blumira.com",
    }
  );
};

const isObject = (value) =>
  _.isObject(value) &&
  !(
    value instanceof String ||
    value instanceof Number ||
    value instanceof Array ||
    value instanceof Function ||
    value instanceof RegExp
  );

export const getOrgDetails = (
  org = {},
  orgTypes = [],
  markets = [],
  licenseTypes = []
) => {
  let orgObj = {};
  if (isObject(org) && !_.isEmpty(org)) {
    const type =
      orgTypes.find((ot) => ot.value === org.orgType)?.label || org.orgType;
    const market =
      markets.find((m) => m.value === org.market)?.label || org.market;
    const license =
      licenseTypes.find((lt) => lt.value === org.license)?.label ||
      org.license ||
      licenseTypes.find((lt) => lt.value === "ADVANCED")?.label;
    orgObj = {
      type: type,
      market: market,
      license: license,
    };
  }
  return orgObj;
};

const PageView = (props) => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const { ChildView, payload = {} } = props;
  const classes = useStyles();
  const [navType, setNavType] = useState("left"); // eslint-disable-line no-unused-vars
  const [theme, setTheme] = useState(themeSelector(props.theme || "default"));

  useEffect(() => {
    new Request("/org", [{ field: "id", value: props.payload.orgId }])
      .get()
      .then((orgArr) => {
        const orgDetails = getOrgDetails(
          orgArr[0],
          ORG_TYPES,
          MARKETS,
          LICENSE_TYPES
        );
        if (!_.isEmpty(orgDetails)) {
          setCustomerInfoCookie(orgDetails);
        } // else don't set a cookie
      })
      .catch((err) => {
        // intentionally letting this fail silently
        // if something goes wrong with the api call
        // by not doing anything with the err
        // since all we're doing here is setting a cookie in the background
        // the end user doesn't need to know it failed
      });
  }, [props.payload.orgId]);

  // if the user changes their theme
  // if theme selection is auto, handle it
  // else set the theme to the selection
  useEffect(() => {
    let isThemeAuto = props.theme === "auto";

    let theme =
      isThemeAuto && prefersDarkMode
        ? darkTheme
        : isThemeAuto && !prefersDarkMode
        ? lightTheme
        : props.theme
        ? props.theme
        : "default";

    setTheme(themeSelector(theme));
  }, [props.theme, prefersDarkMode]);

  let title,
    description = false;
  if (
    language.pages[payload.toplevel] &&
    language.pages[payload.toplevel][payload.secondlevel]
  ) {
    ({ title, description } =
      language.pages[payload.toplevel][payload.secondlevel]);
  }

  return (
    <React.Fragment>
      {<Zendesk />}
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className={classes.root}>
          <AppBar className={classes.appBar}>
            <HeaderView {...props} withNav={navType === "top"} />
            <LicenseRestriction
              features={["AUTOBOT"]}
              renderAllow={(children) => children}
              renderDisallow={() => null}
            >
              <AutoIsolationControlBar />
            </LicenseRestriction>
          </AppBar>
          <SidebarView dispatch={props.dispatch} />
          <Grid
            container
            direction="column"
            spacing={3}
            className={classes.grid}
          >
            <Grid item className={classes.appBarSpacer} />
            <LicenseRestriction
              features={["AUTOBOT"]}
              renderAllow={renderSpacerGridItem}
              renderDisallow={() => null}
            />
            {title && (
              <Grid item>
                <Typography
                  className={classes.pageTitle}
                  component="h1"
                  variant="h5"
                >
                  {title}
                </Typography>
                {description && (
                  <Typography
                    className={clsx(classes.pageTitle, classes.pageSubTitle)}
                    variant="caption"
                  >
                    {description}
                  </Typography>
                )}
                <Divider className={classes.pageTitleDivider} />
              </Grid>
            )}
            <Grid item className={classes.gridItem}>
              <MuiErrorBanner />
              {/* 
              leaving this announcement component for
              future announcements of new releases
              <MuiAnnouncementDialog /> 
              */}
              <ChildView
                ready={props.ready}
                dispatch={props.dispatch}
                {...props.requests}
              />
            </Grid>
          </Grid>
        </div>
      </ThemeProvider>
    </React.Fragment>
  );
};

PageView.propTypes = {
  ChildView: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
  payload: PropTypes.shape({}),
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
};

PageView.defaultProps = {
  ChildView: <div />,
  payload: {},
  error: {
    message: "",
  },
};

const mapStateToProps = (state) => {
  return {
    requests: requestMapper(state),
    ready: state.page.ready,
    payload: state.location.payload,
    theme: state.userInterface.theme,
  };
};

export default connect(mapStateToProps)(PageView);
